import i18n from 'i18next';
import { Reducer } from 'redux';

import {
  AggregationValue,
  AggregationValueWithByCycle,
  MatrixReportResponseRaw,
  MatrixReportSegmentAggregation,
  MatrixResponse,
  SegmentAggregation,
  TopRow,
  TopRowResponseItemRaw,
} from '~src/interfaces/heatmap.interface';
import { ESQuestion } from '~src/interfaces/question.interface';
import {
  CompanySegment,
  getEmptyCompanySegment,
  Segment,
  SegmentType,
} from '~src/interfaces/segment.interface';
import { ESTopic } from '~src/interfaces/topic.interface';
import { isSuccess, ThunkAnyAction } from '~src/services/apiMiddleware';
import { mapIdToData } from '~src/utils';

import {
  fetchOptionsOfAttr,
  fetchQuestionsMatrix,
  fetchSingleGroup,
  fetchSingleTopic,
  fetchTopicsMatrix,
  RESET_MATRIX,
  SURVEY_CHANGE,
} from './action.types';

export interface SelectedGroupObj {
  segment: Segment;
  aggregation: {
    valById: Record<string, AggregationValueWithByCycle | AggregationValue>;
  };
}

export interface HeatmapState {
  abortController?: AbortController;
  loading: boolean;
  loadingTable: boolean;
  loadedTopic: Segment | null;
  topics: ESTopic[];
  questions: ESQuestion[];
  selectedGroup: SelectedGroupObj | null;
  result: MatrixResponse | null;
  topRow?: TopRow;
  survey?: string;
  error: any;
}

export function getInitialCompareState(): HeatmapState {
  return {
    loading: false,
    loadingTable: false,
    loadedTopic: null,
    topics: [],
    questions: [],
    selectedGroup: null,
    result: null,
    error: null,
  };
}

function getCompanySegment(segments: Segment[]): CompanySegment {
  const companyLabel = i18n.t('x.compare.toprow.company');
  return (
    (segments.find((s) => s.type === SegmentType.Company) as CompanySegment) ||
    getEmptyCompanySegment(companyLabel)
  );
}

export const filterResult = (action: ThunkAnyAction<MatrixReportResponseRaw, unknown>): Partial<HeatmapState> => {
  if (!isSuccess(action)) {
    return { result: null, selectedGroup: null };
  }

  const { pagination, result } = action.response.data;
  const { current, segments, segment_aggregations, questions, topics, top_row } = result;

  const compareResponse: MatrixResponse = {
    pagination,
    valueById: mapIdToData((questions || topics) as Array<ESQuestion | ESTopic>),
    aggrBySegmentId: mapIdToData(
      (segment_aggregations || []).map((val: MatrixReportSegmentAggregation) => {
        return {
          id: val.id,
          questions: val.questions,
          topics: val.topics,
          valById: mapIdToData(val.questions! || val.topics),
        };
      }),
    ),
    segmById: mapIdToData(segments),
  };

  const selectedGroup =
    action.selectedGroup && current
      ? {
          segment: current.segment,
          aggregation: {
            valById: mapIdToData(current.segment_aggregation.topics || current.segment_aggregation.questions),
          },
        }
      : null;

  const segment = getCompanySegment(segments);
  const topRowAggs = topRowAggregations(top_row, Boolean(questions), segment);
  const topRow = (topRowAggs && { agg: topRowAggs, segment }) || undefined;

  return { result: compareResponse, selectedGroup, topRow };
};

const heatmapReducer: Reducer<HeatmapState, ThunkAnyAction<MatrixReportResponseRaw, unknown>> = (
  state = getInitialCompareState(),
  action,
): HeatmapState => {
  switch (action.type) {
    case fetchQuestionsMatrix.Request:
    case fetchTopicsMatrix.Request:
    case fetchOptionsOfAttr.Request: {
      return { ...state, loadingTable: true, error: null, abortController: action.abortController };
    }
    case fetchQuestionsMatrix.Success: {
      return {
        ...state,
        loadingTable: false,
        ...filterResult(action),
        questions:
          (action.response.data && (action.response.data as MatrixReportResponseRaw).result.questions) || [],
        abortController: undefined,
      };
    }
    case fetchTopicsMatrix.Success: {
      return {
        ...state,
        loadingTable: false,
        ...filterResult(action),
        topics:
          (action.response.data && (action.response.data as MatrixReportResponseRaw).result.topics) || [],
        abortController: undefined,
      };
    }
    case fetchOptionsOfAttr.Success: {
      return {
        ...state,
        loadingTable: false,
      };
    }
    case fetchQuestionsMatrix.Failed:
    case fetchOptionsOfAttr.Failed:
    case fetchTopicsMatrix.Failed: {
      return { ...state, loadingTable: false, error: action.error, abortController: undefined };
    }
    case fetchSingleGroup.Request: {
      return { ...state, loading: true };
    }
    case fetchSingleTopic.Success: {
      return { ...state, loadedTopic: action.response.data, loading: false };
    }
    case fetchSingleTopic.Failed: {
      return { ...state, loading: false };
    }
    case RESET_MATRIX: {
      return getInitialCompareState();
    }
    case SURVEY_CHANGE: {
      return { ...state, survey: action.payload.survey };
    }
    default:
      return state;
  }
};

export function topRowAggregations(
  topRowResponse: Record<string, TopRowResponseItemRaw>,
  questions: boolean,
  company?: Segment,
): SegmentAggregation | null {
  const aggValuesById = topRowAggVals(topRowResponse);

  if (!Object.keys(aggValuesById).length) {
    return null;
  }
  if (!company) {
    return null;
  }

  return {
    id: company.id,
    questions: (questions && Object.values(aggValuesById)) || undefined,
    topics: (!questions && Object.values(aggValuesById)) || undefined,
    valById: aggValuesById,
  };
}

// converts data from the topRow report to agg values
function topRowAggVals(
  topRowResponse: Record<string, TopRowResponseItemRaw>,
): Record<string, AggregationValueWithByCycle> {
  const result: Record<string, AggregationValueWithByCycle> = {};
  if (!topRowResponse) {
    return result;
  }

  // tslint:disable-next-line: forin
  for (const id in topRowResponse) {
    const element = topRowResponse[id];
    const hasAnswer = typeof element.top_chosen_option === 'number' || element.avg_rating_ord;

    if (!element.id || !element.answer_count || !hasAnswer) {
      continue;
    }
    result[id] = {
      id: element.id,
      average: element.avg_rating_ord,
      top_chosen_option: element.top_chosen_option,
      number_of_answers: element.answer_count,
      by_cycle: [],
    };
  }

  return result;
}

export default heatmapReducer;
